import axios from 'axios'
import qs from 'qs'
import { cloneDeep } from 'lodash-es'
import { AxiosCanceler } from './axiosCancel'
import { isFunction } from '@/utils/isUtils'
import { ContentTypeEnum, RequestEnum } from '@/enums/httpEnum'
import { showLoadingToast, closeToast, showToast } from 'vant'

/**
 * @description:  axios模块
 */
export class VAxios {
  #axiosInstance
  #options

  constructor(options) {
    this.#options = options
    this.#axiosInstance = axios.create(options)
    this.setupInterceptors()
  }

  getAxios() {
    return this.#axiosInstance
  }

  /**
   * @description: 重新配置axios
   */
  configAxios(config) {
    if (!this.#axiosInstance) {
      return
    }
    this.createAxios(config)
  }

  /**
   * @description: 设置通用header
   */
  setHeader(headers) {
    if (!this.#axiosInstance) {
      return
    }

    Object.assign(this.#axiosInstance.defaults.headers, headers)
  }

  /**
   * @description: 请求方法
   */
  request(config, options) {
    let conf = cloneDeep(config)
    const transform = this.getTransform()

    const { requestOptions } = this.#options

    const opt = Object.assign({}, requestOptions, options)

    const { beforeRequestHook, requestCatch, transformRequestData } = transform || {}
    if (beforeRequestHook && isFunction(beforeRequestHook)) {
      conf = beforeRequestHook(conf, opt)
    }

    //这里重新 赋值成最新的配置
    conf.requestOptions = opt
    // 支持 FormData
    conf = this.supportFormData(conf)

    return new Promise((resolve, reject) => {
      showLoadingToast({
        message: '加载中...',
        forbidClick: true
      })
      this.#axiosInstance
        .request(conf)
        .then((res) => {
          // 请求是否被取消
          const isCancel = axios.isCancel(res)
          if (transformRequestData && isFunction(transformRequestData) && !isCancel) {
            if (res.data.success) {
              closeToast()
            } else {
              closeToast()
              if (res.data.message) showToast(res.data.message)
            }
            try {
              const ret = transformRequestData(res, opt)
              resolve(ret)
            } catch (err) {
              reject(err || new Error('request error!'))
            }
            return
          }
          resolve(res)
        })
        .catch((e) => {
          closeToast()
          if (requestCatch && isFunction(requestCatch)) {
            reject(requestCatch(e))
            return
          }
          reject(e)
        })
    })
  }

  /**
   * @description:  创建axios实例
   */
  createAxios(config) {
    this.#axiosInstance = axios.create(config)
  }

  getTransform() {
    const { transform } = this.#options
    return transform
  }

  /**
   * @description:  文件上传
   */
  uploadFile(config, params) {
    const formData = new window.FormData()
    const customFilename = params.name || 'file'

    if (params.filename) {
      formData.append(customFilename, params.file, params.filename)
    } else {
      formData.append(customFilename, params.file)
    }

    if (params.data) {
      Object.keys(params.data).forEach((key) => {
        const value = params.data[key]
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item)
          })
          return
        }

        formData.append(key, params.data[key])
      })
    }

    return this.#axiosInstance.request({
      method: 'POST',
      data: formData,
      headers: {
        'Content-type': ContentTypeEnum.FORM_DATA,
        ignoreCancelToken: true
      },
      ...config
    })
  }

  // 支持 form-data 格式数据
  supportFormData(config) {
    const headers = config.headers || this.#options.headers
    const contentType = headers?.['Content-Type'] || headers?.['content-type']

    if (
      contentType !== ContentTypeEnum.FORM_URLENCODED ||
      !Reflect.has(config, 'data') ||
      config.method?.toUpperCase() === RequestEnum.GET
    ) {
      return config
    }

    return {
      ...config,
      data: qs.stringify(config.data, { arrayFormat: 'brackets' })
    }
  }

  /**
   * @description: 拦截器配置
   */
  setupInterceptors() {
    const transform = this.getTransform()
    if (!transform) {
      return
    }
    const {
      requestInterceptors,
      requestInterceptorsCatch,
      responseInterceptors,
      responseInterceptorsCatch
    } = transform

    const axiosCanceler = new AxiosCanceler()

    // 请求拦截器配置处理
    this.#axiosInstance.interceptors.request.use((config) => {
      const {
        headers: { ignoreCancelToken }
      } = config

      const ignoreCancel =
        ignoreCancelToken !== undefined
          ? ignoreCancelToken
          : config.requestOptions?.ignoreCancelToken
      !ignoreCancel && axiosCanceler.addPending(config)
      if (requestInterceptors && isFunction(requestInterceptors)) {
        config = requestInterceptors(config, this.#options)
      }
      return config
    }, undefined)

    // 请求拦截器错误捕获
    requestInterceptorsCatch &&
      isFunction(requestInterceptorsCatch) &&
      this.#axiosInstance.interceptors.request.use(undefined, requestInterceptorsCatch)

    // 响应结果拦截器处理
    this.#axiosInstance.interceptors.response.use(
      (res) => {
        axiosCanceler.removePending(res.config)
        res && axiosCanceler.removePending(res.config)
        if (responseInterceptors && isFunction(responseInterceptors)) {
          res = responseInterceptors(res)
        }
        return res
      },
      (error) => {
        const { config } = error
        axiosCanceler.removePending(config || {})
        if (axios.isCancel(error)) {
          console.warn('已取消的重复请求：', error.message)
          return
        }
        return undefined
      }
    )

    // 响应结果拦截器错误捕获
    responseInterceptorsCatch &&
      isFunction(responseInterceptorsCatch) &&
      this.#axiosInstance.interceptors.response.use(undefined, responseInterceptorsCatch)
  }
}
